import React from 'react';
import ReactDOM from 'react-dom/client';
import { createHashRouter, Navigate, RouterProvider, useParams } from 'react-router-dom';

import Providers from 'providers/Providers';
import OrgFleetProvider from 'providers/OrgFleetProvider';
import ARCProvider from 'providers/ARC/ARCProvider';
import FleetARCProvider from 'providers/ARC/FleetARCProvider';
import FeaturesProvider from 'providers/FeaturesProvider';
import GimbalControlProvider from 'providers/GimbalControlProvider';
import ToastProvider from 'providers/ToastProvider';
import UserProvider from 'providers/UserProvider';
import NotificationProvider from 'providers/NotificationProvider';
import MultiARCsControlProvider from 'providers/MultiARCsControlProvider';
import NavigationProvider from 'providers/navigation/NavigationProvider';
import DockingProvider from 'providers/DockingProvider';
import MissionExecutionProvider from 'providers/navigation/MissionExecutionProvider';
import ReduxStoreProvider from 'providers/ReduxStoreProvider';

import WebAppLayout from 'layout/WebAppLayout';
import NotFoundPage from 'pages/NotFoundPage';

import { createArcControlRoute } from 'routes/arcControl';
import { dashboardRoutes, mapRoutes, fleetRoutes } from 'routes';
import 'i18n';
import './index.css';

document.title = 'R2C2 Fleet Control';

// Redirect /fleet/control/:serial (old route, called by ARCProvider.selectARC) to the new route.
const FleetControlRedirect = () => {
    const { ARCSerialNumber } = useParams<{ ARCSerialNumber: string }>();
    return <Navigate to={`/fleet/arc/${ARCSerialNumber}/control`} replace />;
};

const fleetControlRedirectRoute = {
    path: 'control/:ARCSerialNumber',
    element: <FleetControlRedirect />,
};

const fleetArcControlRoute = createArcControlRoute({
    basePath: 'fleet/arc/:arcId',
    arcProvider: FleetARCProvider,
    leadingProviders: [UserProvider, OrgFleetProvider],
});

const router = createHashRouter([
    {
        index: true,
        element: <Navigate to="/fleet/dashboard" replace />,
    },
    {
        path: 'fleet',
        element: (
            <Providers
                providers={[
                    ReduxStoreProvider,
                    NotificationProvider,
                    ToastProvider,
                    UserProvider,
                    OrgFleetProvider,
                    MultiARCsControlProvider,
                    ARCProvider,
                    FeaturesProvider,
                    GimbalControlProvider,
                    NavigationProvider,
                    DockingProvider,
                    MissionExecutionProvider,
                ]}
            >
                <WebAppLayout />
            </Providers>
        ),
        children: [dashboardRoutes, fleetControlRedirectRoute, mapRoutes, fleetRoutes],
    },
    {
        ...fleetArcControlRoute,
    },
    {
        path: '*',
        element: <NotFoundPage />,
    },
]);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
    <React.StrictMode>
        <RouterProvider router={router} />
    </React.StrictMode>
);
